import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Calculator, Send, ExternalLink, FileText, Wallet } from "lucide-react";
import api from "@/lib/api";

const CONDITION = [{ label: "Light", v: 0.9 }, { label: "Standard", v: 1.0 }, { label: "Heavy", v: 1.3 }];
const STORY = [{ label: "1 story", v: 1.0 }, { label: "2 story", v: 1.2 }, { label: "3 story", v: 1.45 }];
const unitLabel = { sqft: "sq ft", linft: "linear ft", unit: "unit(s)" };
const DEPOSIT_MIN = 500;

const inputCls = "w-full rounded-xl border border-border bg-muted/50 px-3 py-2.5 text-sm focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary";

const TYPE_BADGE = {
  full: "bg-muted text-muted-foreground",
  deposit: "bg-primary/20 text-primary",
  balance: "bg-secondary/20 text-secondary",
};

export default function InvoiceTab() {
  const [invoices, setInvoices] = useState([]);
  const [services, setServices] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [taxPercent, setTaxPercent] = useState(6);
  const [daysDue, setDaysDue] = useState(7);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [balancingId, setBalancingId] = useState(null);

  // deposit state
  const [deposit, setDeposit] = useState(false);
  const [depositPercent, setDepositPercent] = useState(50);

  // estimator state
  const [svc, setSvc] = useState("");
  const [measure, setMeasure] = useState("");
  const [qty, setQty] = useState(1);
  const [cond, setCond] = useState(1.0);
  const [story, setStory] = useState(1.0);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/invoices"); setInvoices(data.invoices); }
    catch { toast.error("Couldn't load invoices"); }
  }, []);
  const loadServices = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/service-presets");
      setServices(data.presets);
      setSvc((cur) => cur || (data.presets[0]?.id ?? ""));
    } catch { toast.error("Couldn't load services"); }
  }, []);
  useEffect(() => { load(); loadServices(); }, [load, loadServices]);

  const service = services.find((s) => s.id === svc) || services[0];

  const addFromEstimator = () => {
    if (!service) return;
    if (service.unit === "unit") {
      const q = Math.max(1, parseInt(qty, 10) || 1);
      setLines((l) => [...l, { description: service.label, amount: service.rate, quantity: q }]);
    } else {
      const m = parseFloat(measure);
      if (!m || m <= 0) { toast.error(`Enter ${unitLabel[service.unit]}`); return; }
      let amt = m * service.rate * (service.mult ? cond : 1) * (service.unit === "sqft" ? story : 1);
      amt = Math.max(amt, service.min);
      const cparts = [];
      if (service.mult) cparts.push(CONDITION.find((c) => c.v === cond)?.label.toLowerCase());
      if (service.unit === "sqft") cparts.push(STORY.find((s) => s.v === story)?.label);
      const desc = `${service.label} — ${m} ${unitLabel[service.unit]}${cparts.length ? " (" + cparts.join(", ") + ")" : ""}`;
      setLines((l) => [...l, { description: desc, amount: Math.round(amt * 100) / 100, quantity: 1 }]);
    }
    setMeasure(""); setQty(1);
  };

  const addCustom = () => setLines((l) => [...l, { description: "", amount: 0, quantity: 1 }]);
  const updateLine = (i, field, val) => setLines((l) => l.map((ln, idx) => idx === i ? { ...ln, [field]: field === "description" ? val : Number(val) } : ln));
  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const subtotal = lines.reduce((s, l) => s + (l.amount || 0) * (l.quantity || 1), 0);
  const tax = subtotal * (Number(taxPercent) || 0) / 100;
  const total = subtotal + tax;
  const canDeposit = total >= DEPOSIT_MIN;
  const useDeposit = deposit && canDeposit;
  const depositAmt = useDeposit ? total * (Number(depositPercent) || 0) / 100 : 0;
  const balanceAmt = total - depositAmt;

  const submit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Enter customer name and email"); return; }
    if (lines.length === 0) { toast.error("Add at least one line item"); return; }
    if (lines.some((l) => !l.description.trim() || l.amount <= 0)) { toast.error("Each line needs a description and amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices", {
        customer_name: name, customer_email: email,
        tax_percent: Number(taxPercent) || 0, days_until_due: Number(daysDue) || 0,
        notes: notes || null, line_items: lines,
        deposit: useDeposit, deposit_percent: Math.min(99, Math.max(1, Number(depositPercent) || 50)),
      });
      toast.success(useDeposit ? "Deposit invoice created & emailed" : "Invoice created & emailed to customer");
      setName(""); setEmail(""); setNotes(""); setLines([]); setDeposit(false); setDepositPercent(50);
      setInvoices((iv) => [data.invoice, ...iv]);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create invoice"); }
    finally { setBusy(false); }
  };

  const sendBalance = async (iv) => {
    setBalancingId(iv.id);
    try {
      const { data } = await api.post(`/admin/invoices/${iv.id}/balance`);
      toast.success("Balance invoice sent to customer");
      setInvoices((list) => [data.invoice, ...list.map((x) => x.id === iv.id ? { ...x, balance_status: "sent", balance_invoice_id: data.invoice.id } : x)]);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send balance"); }
    finally { setBalancingId(null); }
  };

  return (
    <div className="space-y-8" data-testid="invoices-panel">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Estimator */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-primary" /><h3 className="font-heading text-lg font-bold">Estimator</h3></div>
          <p className="mt-1 text-xs text-muted-foreground">Rates are editable per line after you add them. Manage services in the Services & Rates tab.</p>
          <label className="mt-4 block text-sm font-semibold">Service</label>
          <select data-testid="estimator-service" value={svc} onChange={(e) => setSvc(e.target.value)} className={inputCls}>
            {services.map((s) => <option key={s.id} value={s.id}>{s.label} — ${s.rate}{s.unit === "unit" ? "/ea" : "/" + unitLabel[s.unit]}{s.min ? ` (min $${s.min})` : ""}</option>)}
          </select>
          {service && service.unit === "unit" ? (
            <div className="mt-3">
              <label className="block text-sm font-semibold">Quantity</label>
              <input data-testid="estimator-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} />
            </div>
          ) : service && (
            <>
              <div className="mt-3">
                <label className="block text-sm font-semibold">{service.unit === "linft" ? "Linear feet" : "Square feet"}</label>
                <input data-testid="estimator-measure" type="number" min="0" value={measure} onChange={(e) => setMeasure(e.target.value)} className={inputCls} placeholder="e.g. 1200" />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold">Condition</label>
                  <select data-testid="estimator-condition" value={cond} onChange={(e) => setCond(Number(e.target.value))} className={inputCls}>
                    {CONDITION.map((c) => <option key={c.label} value={c.v}>{c.label}</option>)}
                  </select>
                </div>
                {service.unit === "sqft" && (
                  <div>
                    <label className="block text-sm font-semibold">Stories</label>
                    <select data-testid="estimator-story" value={story} onChange={(e) => setStory(Number(e.target.value))} className={inputCls}>
                      {STORY.map((s) => <option key={s.label} value={s.v}>{s.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </>
          )}
          <button data-testid="estimator-add" onClick={addFromEstimator} disabled={!service} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:brightness-110 disabled:opacity-50"><Plus className="h-4 w-4" /> Add to invoice</button>
          <button data-testid="add-custom-line" onClick={addCustom} className="mt-2 w-full rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent/40">+ Add custom line</button>
        </div>

        {/* Invoice builder */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-heading text-lg font-bold">New Invoice</h3>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <input data-testid="invoice-customer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" className={inputCls} />
            <input data-testid="invoice-customer-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Customer email" className={inputCls} />
          </div>
          <div className="mt-4 space-y-2">
            {lines.length === 0 && <p className="text-sm text-muted-foreground">No line items yet — use the estimator.</p>}
            {lines.map((ln, i) => (
              <div key={i} data-testid={`invoice-line-${i}`} className="flex items-center gap-2 rounded-lg bg-muted/40 p-2">
                <input value={ln.description} onChange={(e) => updateLine(i, "description", e.target.value)} placeholder="Description" className="flex-1 rounded-md border border-border bg-card px-2 py-1.5 text-sm" />
                <input type="number" min="1" value={ln.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="w-14 rounded-md border border-border bg-card px-2 py-1.5 text-sm" title="Qty" />
                <div className="flex items-center"><span className="text-sm text-muted-foreground">$</span><input type="number" min="0" step="0.01" value={ln.amount} onChange={(e) => updateLine(i, "amount", e.target.value)} className="w-20 rounded-md border border-border bg-card px-2 py-1.5 text-sm" /></div>
                <button data-testid={`remove-line-${i}`} onClick={() => removeLine(i)} className="rounded-md p-1.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-semibold">Tax %</label><input data-testid="invoice-tax" type="number" min="0" step="0.1" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} className={inputCls} /></div>
            <div><label className="block text-xs font-semibold">Due in (days)</label><input data-testid="invoice-due" type="number" min="0" value={daysDue} onChange={(e) => setDaysDue(e.target.value)} className={inputCls} /></div>
          </div>
          <textarea data-testid="invoice-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)" className={`mt-3 ${inputCls}`} />

          {/* Deposit */}
          <div className={`mt-4 rounded-xl border p-3 ${canDeposit ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"}`}>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input data-testid="deposit-toggle" type="checkbox" checked={useDeposit} disabled={!canDeposit} onChange={(e) => setDeposit(e.target.checked)} className="h-4 w-4 accent-primary disabled:opacity-40" />
              <Wallet className="h-4 w-4 text-primary" /> Collect a deposit now
            </label>
            {canDeposit ? (
              <>
                <div className="mt-3 flex items-center gap-2">
                  <label className="text-xs font-semibold">Deposit %</label>
                  <input data-testid="deposit-percent" type="number" min="1" max="99" value={depositPercent} onChange={(e) => setDepositPercent(e.target.value)} disabled={!deposit} className="w-20 rounded-md border border-border bg-card px-2 py-1.5 text-sm disabled:opacity-50" />
                </div>
                {useDeposit && (
                  <div className="mt-2 space-y-0.5 text-xs">
                    <div className="flex justify-between text-primary font-bold"><span>Deposit due now</span><span data-testid="deposit-amount">${depositAmt.toFixed(2)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Balance on completion</span><span data-testid="balance-amount">${balanceAmt.toFixed(2)}</span></div>
                  </div>
                )}
              </>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">Deposits are available on jobs of ${DEPOSIT_MIN}+. This job is ${total.toFixed(2)}.</p>
            )}
          </div>

          <div className="mt-4 space-y-1 border-t border-border pt-3 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="invoice-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Tax ({Number(taxPercent) || 0}%)</span><span>${tax.toFixed(2)}</span></div>
            <div className="flex justify-between font-bold"><span>{useDeposit ? "Job total" : "Total"}</span><span data-testid="invoice-total">${total.toFixed(2)}</span></div>
          </div>
          <button data-testid="invoice-send" onClick={submit} disabled={busy} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-secondary px-6 py-3 font-extrabold text-secondary-foreground shadow-[0_0_18px_rgba(57,255,20,0.3)] hover:brightness-110 disabled:opacity-50">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4" /> {useDeposit ? "Send Deposit Invoice" : "Create & Send Invoice"}</>}
          </button>
        </div>
      </div>

      {/* Sent invoices */}
      <div>
        <h3 className="font-heading text-lg font-bold">Invoices</h3>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Created</th><th className="px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {invoices.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No invoices yet.</td></tr>}
              {invoices.map((iv) => {
                const itype = iv.invoice_type || "full";
                const amountDue = iv.amount_due ?? iv.total ?? 0;
                return (
                  <tr key={iv.id} data-testid={`invoice-row-${iv.id}`} className="border-t border-border/50 align-top">
                    <td className="px-4 py-3 font-mono text-xs">{iv.number || "—"}</td>
                    <td className="px-4 py-3">{iv.customer_name}<div className="text-xs text-muted-foreground">{iv.customer_email}</div></td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${TYPE_BADGE[itype]}`}>{itype}</span></td>
                    <td className="px-4 py-3 font-semibold">
                      ${amountDue.toFixed(2)}
                      {itype === "deposit" && <div className="text-xs font-normal text-muted-foreground">of ${(iv.total || 0).toFixed(2)} · bal ${(iv.balance_amount || 0).toFixed(2)}</div>}
                    </td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${iv.status === "paid" ? "bg-secondary/20 text-secondary" : "bg-primary/20 text-primary"}`}>{iv.status}</span></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{(iv.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {iv.hosted_invoice_url && <a href={iv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary hover:underline" title="Open pay page"><ExternalLink className="h-4 w-4" /></a>}
                        {iv.invoice_pdf && <a href={iv.invoice_pdf} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" title="PDF"><FileText className="h-4 w-4" /></a>}
                        {itype === "deposit" && iv.balance_status !== "sent" && (
                          <button data-testid={`send-balance-${iv.id}`} onClick={() => sendBalance(iv)} disabled={balancingId === iv.id} className="inline-flex items-center gap-1 rounded-lg bg-secondary px-2.5 py-1 text-xs font-bold text-secondary-foreground hover:brightness-110 disabled:opacity-50">
                            {balancingId === iv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wallet className="h-3 w-3" />} Send Balance
                          </button>
                        )}
                        {itype === "deposit" && iv.balance_status === "sent" && <span className="text-xs font-semibold text-secondary">Balance sent</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
